import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { invoiceAPI } from '../../services/api';
import { TableSkeleton } from '../../components/skeletons';

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 24 },
  card: { padding: 20, borderRadius: 12, background: '#fff', border: '1px solid var(--border)', marginBottom: 12, textDecoration: 'none', color: 'inherit', display: 'block' },
  statusBadge: (status) => ({
    display: 'inline-block', padding: '4px 14px', borderRadius: 14, fontSize: 11, fontWeight: 700,
    background: status === 'paid' ? '#E8F5EE' : status === 'cancelled' ? '#FEE2E2' : '#FFF8E7',
    color: status === 'paid' ? '#1B5C3A' : status === 'cancelled' ? '#DC2626' : '#B88200',
  }),
  kindBadge: (kind) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 700,
    background: kind === 'wallet-recharge' ? '#E8F5EE' : '#E0E7FF',
    color: kind === 'wallet-recharge' ? '#1B5C3A' : '#4338CA',
    textTransform: 'uppercase', letterSpacing: 0.4,
  }),
  row: { display: 'flex', gap: 20, fontSize: 12, color: 'var(--muted)', flexWrap: 'wrap', marginTop: 8 },
  emptyBox: { textAlign: 'center', padding: '48px 20px', background: '#fff', borderRadius: 12, border: '1px solid var(--border)' },
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    invoiceAPI.list()
      .then(r => setInvoices(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmtAmt = (v) => v != null ? `\u20B9${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-';
  const formatPeriod = (inv) => {
    if (inv.kind === 'wallet-recharge') {
      return new Date(inv.issuedAt || inv.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    if (inv.period?.month && inv.period?.year) {
      const d = new Date(inv.period.year, inv.period.month - 1, 1);
      return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    }
    return '-';
  };

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.title}>Invoices</div>
        <div style={S.subtitle}>Your tax invoices — click any to view the full GST breakdown and print.</div>
        <TableSkeleton rows={5} columns={4} />
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.title}>Invoices</div>
      <div style={S.subtitle}>Your tax invoices — click any to view the full GST breakdown and print.</div>

      {invoices.length === 0 ? (
        <div style={S.emptyBox}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🧾</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)', marginBottom: 4 }}>No invoices yet</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>An invoice is issued automatically the next time you recharge your wallet.</div>
        </div>
      ) : (
        invoices.map(inv => {
          const hasIGST = inv.igst != null && inv.igst > 0;
          return (
            <Link key={inv._id} to={`/vendor/invoices/${inv._id}`} style={S.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--char)' }}>
                      {inv.invoiceNumber || `INV-${inv._id?.slice(-6)?.toUpperCase()}`}
                    </span>
                    <span style={S.kindBadge(inv.kind || 'wallet-recharge')}>
                      {inv.kind === 'monthly-leads' ? 'Monthly leads' : 'Wallet recharge'}
                    </span>
                    <span style={S.statusBadge(inv.status || 'issued')}>{inv.status || 'issued'}</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>
                    {inv.kind === 'wallet-recharge' ? 'Issued' : 'Period'}: {formatPeriod(inv)}
                  </div>
                  <div style={S.row}>
                    <span>Taxable: {fmtAmt(inv.taxableValue)}</span>
                    {hasIGST ? (
                      <span>IGST {inv.totalRate || 18}%: {fmtAmt(inv.igst)}</span>
                    ) : (
                      <>
                        <span>CGST: {fmtAmt(inv.cgst)}</span>
                        <span>SGST: {fmtAmt(inv.sgst)}</span>
                      </>
                    )}
                    {inv.gstInclusive && <span style={{ color: '#1B5C3A' }}>GST-inclusive</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: 'var(--char)' }}>
                    {fmtAmt(inv.amountPaid)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Total</div>
                </div>
              </div>
            </Link>
          );
        })
      )}
    </div>
  );
}
